import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  CheckCircle2, 
  XCircle, 
  Clock,
  LogOut,
  FolderOpen,
  Settings,
  UserCheck,
  UserX,
  Ban,
  Home,
  Upload,
  BarChart3,
  Trash2,
  ShieldCheck,
  ShieldOff,
  Search,
  Library,
  Briefcase,
  Receipt
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar Component
const Sidebar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <aside className="w-72 bg-gradient-to-b from-[#0F172A] to-[#1E293B] min-h-screen fixed left-0 top-0 flex flex-col">
      {/* Logo Header */}
      <div className="p-6 border-b border-white/10">
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
          data-testid="nav-home"
        >
          <div className="w-10 h-10 relative rounded-lg overflow-hidden bg-white flex-shrink-0">
            <img src="/aks-logo.png" alt="AKS" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-white leading-tight">AKS TAX AND LEGAL<br/>CONSULTANCY</h1>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">Menu</p>
        <ul className="space-y-1">
          <li>
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-home-btn"
            >
              <Home className="w-5 h-5" />
              Home
            </button>
          </li>
          <li>
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-documents"
            >
              <FolderOpen className="w-5 h-5" />
              All Notices
            </button>
          </li>
          <li>
            <button 
              onClick={() => navigate('/research')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-research"
            >
              <Search className="w-5 h-5" />
              Legal Research
            </button>
          </li>
          <li>
            <button 
              onClick={() => navigate('/library')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-library"
            >
              <Library className="w-5 h-5" />
              Legal Library
            </button>
          </li>
          <li>
            <button 
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white shadow-lg"
              data-testid="nav-admin"
            >
              <Users className="w-5 h-5" />
              User Management
            </button>
          </li>
          <li>
            <button 
              onClick={() => navigate('/admin/usage')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-usage"
            >
              <BarChart3 className="w-5 h-5" />
              Usage & Costs
            </button>
          </li>
        </ul>

        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 mt-6 px-3">Billing</p>
        <ul className="space-y-1">
          <li>
            <button
              onClick={() => navigate('/clients')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-clients"
            >
              <Briefcase className="w-5 h-5" />
              Clients
            </button>
          </li>
          <li>
            <button
              onClick={() => navigate('/invoices')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-invoices"
            >
              <Receipt className="w-5 h-5" />
              Invoices
            </button>
          </li>
        </ul>
        
        {/* Quick Action - Upload Notice */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">Quick Actions</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white shadow-lg hover:shadow-xl transition-all"
            data-testid="quick-upload-btn"
          >
            <Upload className="w-5 h-5" />
            Upload Notice
          </button>
        </div>
      </nav>
      
      {/* User Profile */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4 p-3 bg-white/5 rounded-xl">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full ring-2 ring-[#F59E0B]" />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center text-white font-medium ring-2 ring-[#F59E0B]">
              {user?.name?.charAt(0) || 'A'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className={`text-xs font-medium ${user?.role === 'super_admin' ? 'text-purple-300' : 'text-[#F59E0B]'}`}>
              {user?.role === 'super_admin' ? 'Super Admin' : 'Administrator'}
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          className="w-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`);
      toast.success('User approved successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`);
      toast.success('User rejected');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const handleRevoke = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke access for this user?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/revoke`);
      toast.success('User access revoked');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('This will permanently delete this user. Are you sure?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted permanently');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleMakeAdmin = async (userId) => {
    if (!window.confirm('This will give this user full admin privileges. Continue?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/make-admin`);
      toast.success('User promoted to admin');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm('This will remove admin privileges from this user. Continue?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/remove-admin`);
      toast.success('Admin demoted to regular user');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleMakeSuperAdmin = async (userId) => {
    if (!window.confirm(
      'This will give the user SUPER ADMIN privileges, including the ability to hard-delete PAID tax invoices. Continue?',
    )) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/make-super-admin`);
      toast.success('User promoted to Super Admin');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote to super_admin');
    }
  };

  const handleRemoveSuperAdmin = async (userId) => {
    if (!window.confirm('This will demote the Super Admin back to a regular Admin. Continue?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/remove-super-admin`);
      toast.success('Super Admin demoted to Admin');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to demote super_admin');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="badge-approved flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approved</span>;
      case 'pending':
        return <span className="badge-pending flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case 'rejected':
        return <span className="badge-rejected flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>;
      default:
        return <span className="badge-pending">{status}</span>;
    }
  };

  const filteredUsers = users.filter(u => {
    if (filter === 'all') return true;
    return u.status === filter;
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;
  const approvedCount = users.filter(u => u.status === 'approved').length;
  const rejectedCount = users.filter(u => u.status === 'rejected').length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Sidebar user={user} onLogout={logout} />
      
      <main className="ml-72 p-8" data-testid="admin-dashboard">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              User Management
            </h1>
            <p className="text-slate-600">
              Approve or reject user access requests
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <Card 
              className={`cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-yellow-400' : ''}`}
              onClick={() => setFilter('pending')}
              data-testid="pending-filter-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600">{pendingCount}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-green-400' : ''}`}
              onClick={() => setFilter('approved')}
              data-testid="approved-filter-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Approved</p>
                    <p className="text-3xl font-bold text-green-600">{approvedCount}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-red-400' : ''}`}
              onClick={() => setFilter('rejected')}
              data-testid="rejected-filter-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Rejected</p>
                    <p className="text-3xl font-bold text-red-600">{rejectedCount}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'btn-primary' : ''}
              data-testid="filter-all"
            >
              All Users ({users.length})
            </Button>
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              onClick={() => setFilter('pending')}
              className={filter === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}
              data-testid="filter-pending"
            >
              Pending ({pendingCount})
            </Button>
            <Button
              variant={filter === 'approved' ? 'default' : 'outline'}
              onClick={() => setFilter('approved')}
              className={filter === 'approved' ? 'bg-green-500 hover:bg-green-600 text-white' : ''}
              data-testid="filter-approved"
            >
              Approved ({approvedCount})
            </Button>
            <Button
              variant={filter === 'rejected' ? 'default' : 'outline'}
              onClick={() => setFilter('rejected')}
              className={filter === 'rejected' ? 'bg-red-500 hover:bg-red-600 text-white' : ''}
              data-testid="filter-rejected"
            >
              Rejected ({rejectedCount})
            </Button>
          </div>

          {/* Users Table */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#1E3A8A]" />
                Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-slate-100 rounded skeleton"></div>
                  ))}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No users found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 bg-[#1E3A8A] rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {u.name?.charAt(0) || 'U'}
                              </div>
                            )}
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{u.email}</TableCell>
                        <TableCell>{getStatusBadge(u.status)}</TableCell>
                        <TableCell>
                          {u.role === 'super_admin' ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
                              <ShieldCheck className="w-3 h-3" />
                              Super Admin
                            </span>
                          ) : u.role === 'admin' ? (
                            <span className="text-xs font-medium text-[#F59E0B]">Admin</span>
                          ) : (
                            <span className="text-xs font-medium text-slate-500">User</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end flex-wrap">
                            {/* Pending users: Approve / Reject (not admins/super_admins) */}
                            {u.status === 'pending' && u.role !== 'admin' && u.role !== 'super_admin' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  onClick={() => handleApprove(u.user_id)}
                                  data-testid={`approve-${u.user_id}`}
                                >
                                  <UserCheck className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleReject(u.user_id)}
                                  data-testid={`reject-${u.user_id}`}
                                >
                                  <UserX className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {/* Approved non-admin users: Make Admin / Revoke */}
                            {u.status === 'approved' && u.role !== 'admin' && u.role !== 'super_admin' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                  onClick={() => handleMakeAdmin(u.user_id)}
                                  data-testid={`make-admin-${u.user_id}`}
                                >
                                  <ShieldCheck className="w-4 h-4 mr-1" />
                                  Make Admin
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleRevoke(u.user_id)}
                                  data-testid={`revoke-${u.user_id}`}
                                >
                                  <Ban className="w-4 h-4 mr-1" />
                                  Revoke
                                </Button>
                              </>
                            )}
                            {/* Admin user (not self): Remove Admin + (super_admin only) Make Super Admin */}
                            {u.role === 'admin' && u.user_id !== user?.user_id && (
                              <>
                                {user?.role === 'super_admin' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-purple-700 border-purple-200 hover:bg-purple-50"
                                    onClick={() => handleMakeSuperAdmin(u.user_id)}
                                    data-testid={`make-super-admin-${u.user_id}`}
                                  >
                                    <ShieldCheck className="w-4 h-4 mr-1" />
                                    Make Super Admin
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                  onClick={() => handleRemoveAdmin(u.user_id)}
                                  data-testid={`remove-admin-${u.user_id}`}
                                >
                                  <ShieldOff className="w-4 h-4 mr-1" />
                                  Remove Admin
                                </Button>
                              </>
                            )}
                            {/* Super Admin user (not self): Demote Super Admin — only super_admins see this */}
                            {u.role === 'super_admin' && u.user_id !== user?.user_id && user?.role === 'super_admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-700 border-purple-200 hover:bg-purple-50"
                                onClick={() => handleRemoveSuperAdmin(u.user_id)}
                                data-testid={`remove-super-admin-${u.user_id}`}
                              >
                                <ShieldOff className="w-4 h-4 mr-1" />
                                Demote Super Admin
                              </Button>
                            )}
                            {/* Rejected users: Re-approve / Delete */}
                            {u.status === 'rejected' && u.role !== 'admin' && u.role !== 'super_admin' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  onClick={() => handleApprove(u.user_id)}
                                  data-testid={`reapprove-${u.user_id}`}
                                >
                                  <UserCheck className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleDelete(u.user_id)}
                                  data-testid={`delete-${u.user_id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </>
                            )}
                            {/* Self indicator */}
                            {u.user_id === user?.user_id && (
                              <span className="text-xs text-slate-400 italic px-2 py-1">You</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
